import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { EmptyState } from "@/components/app/lists/empty-state";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { SearchField } from "@/components/app/patterns/search-field";
import { TableCard } from "@/components/app/patterns/table-card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { OptionSelect, SelectOption } from "@/components/ui/option-select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { optionalOneOf, optionalTrue, searchPatch, optionalSearch } from "@/lib/search";
import { Plus } from "@/lib/icons";
import { keepWhileSame } from "@/lib/query/keep-while-same";
import { orpc } from "@/lib/orpc";
import { useCanOrganizationWide } from "@/lib/policy";
import { MenusPager } from "@/routes/_authenticated/menus/-components/menus-pager";
import { usePaging } from "@/routes/_authenticated/menus/-lib/use-paging";
import { SUPPLY_CATEGORIES } from "@alm/domain";
import { useKitchen } from "@/routes/_authenticated/menus/-lib/use-kitchen";
import { ClosetDialog } from "./-components/closet-dialog";
import { ProductForm } from "./-components/product-form";
import { ProductsTable } from "./-components/products-table";
import { SupplyCategoryOptions } from "./-components/supply-category-options";
import type { ProductGroup } from "./-lib/product-groups";

export const Route = createFileRoute("/_authenticated/supplies/products")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { q?: string; group?: ProductGroup; food?: true } => ({
    q: optionalSearch(search.q),
    group: optionalOneOf([...SUPPLY_CATEGORIES, "unsorted", "food"] as const, search.group),
    food: optionalTrue(search.food),
  }),
  component: ProductsPage,
});

/** Every product Nancy knows, as soon as she meets it, beside the organization's own. */
function ProductsPage() {
  const { facilityId, canManage } = useKitchen();
  const keepsProducts = useCanOrganizationWide("menu:manage");
  const { q, group: pickedGroup, food: pickedFood } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const {
    text: search,
    change: setSearch,
    settling,
  } = useSearchParamText(q, (next) => patch({ q: next }));
  const group: ProductGroup | "" = pickedGroup ?? "";
  const setGroup = (next: ProductGroup | "") => patch({ group: next || undefined });
  const food = Boolean(pickedFood);
  const setFood = (next: boolean) => patch({ food: next ? true : undefined });
  const [adding, setAdding] = useState(false);
  const paging = usePaging(100);
  const products = useQuery({
    ...orpc.agents.supplies.products.list.queryOptions({
      input: {
        facilityId,
        limit: paging.size,
        offset: paging.offset,
        show: food ? "all" : "supplies",
        ...(q ? { search: q } : {}),
        ...(group ? { category: group } : {}),
      },
    }),
    placeholderData: keepWhileSame({ facilityId }),
  });
  const searching = settling || products.isPlaceholderData;
  const pick = (next: ProductGroup | "") => {
    setGroup(next);
    paging.reset();
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <SearchField
          className="max-w-xs sm:w-full"
          value={search}
          onChange={(next) => {
            setSearch(next);
            paging.reset();
          }}
          placeholder="Search products"
        />
        <OptionSelect
          aria-label="Category"
          value={group}
          className="w-48"
          onChange={(event) => pick(event.target.value as ProductGroup | "")}
        >
          <SelectOption value="">Every category</SelectOption>
          <SupplyCategoryOptions />
          <SelectOption value="unsorted">Not sorted yet</SelectOption>
          {food ? <SelectOption value="food">Food</SelectOption> : null}
        </OptionSelect>
        <div className="flex items-center gap-2">
          <Switch
            id="supplies-show-food"
            checked={food}
            onCheckedChange={(next) => {
              setFood(next);
              if (!next && group === "food") pick("");
              else paging.reset();
            }}
          />
          <Label htmlFor="supplies-show-food" className="text-sm font-normal">
            Show food too
          </Label>
        </div>
        {keepsProducts ? (
          <div className="ml-auto">
            <ClosetDialog
              open={adding}
              onOpenChange={setAdding}
              title="Add a product"
              description="One of your own, for every building: something Nancy has not met yet, or the way you buy it."
              trigger={
                <Button size="sm">
                  <Plus className="size-3.5" />
                  Add product
                </Button>
              }
            >
              <ProductForm facilityId={facilityId} start={{}} onSaved={() => setAdding(false)} />
            </ClosetDialog>
          </div>
        ) : null}
      </div>

      {products.isPending ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : products.isError ? (
        <ReadFailed
          message="Couldn't load the products."
          error={products.error}
          onRetry={() => void products.refetch()}
        />
      ) : products.data.rows.length === 0 && searching ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : products.data.rows.length === 0 ? (
        <EmptyState
          title={search || group ? "No products match" : "No products yet"}
          description={
            search || group
              ? "Check the spelling, or pick another category."
              : "Products appear here as Nancy meets them in stores."
          }
        />
      ) : (
        <TableCard busy={searching}>
          <ProductsTable
            facilityId={facilityId}
            canManage={canManage}
            products={products.data.rows}
          />
          <MenusPager paging={paging} total={products.data.total} noun={["product", "products"]} />
        </TableCard>
      )}
    </section>
  );
}
